import { ConfiguredCarCarCode } from '@oneaudi/onegraph-client';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useAppDispatch, useAppState } from '~/app/contexts/app-context';
import { useMarketConfig } from '~/app/market-config';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useConfiguredCarVehicleCodeLazyQuery } from '~/graphql/hooks/use-configured-car-queries';
import { state } from '~/storage';

export function useGetAudiCode() {
	const {
		config: { financingInput },
	} = useMarketConfig();
	const { carIdentifier } = useConfigurator();
	const dispatch = useAppDispatch();
	const appState = useAppState();
	const audiCode = appState.audiCode?.data ?? ({} as ConfiguredCarCarCode);
	const shouldDisableButton = appState.audiCode?.loading;
	const [reloadAudicode, setReloadAudicode] = useAtom(state.reloadAudicode);

	const { fetchAudicode, loading: isLoadingAudiCode } = useConfiguredCarVehicleCodeLazyQuery();

	const dispatchAudiCode = useCallback(
		(newAudiCode: ConfiguredCarCarCode, loading: boolean) => {
			dispatch({ type: 'SET_AUDICODE', payload: { data: newAudiCode, loading } });
		},
		[dispatch],
	);

	const getAudiCode = useCallback(
		async (callback: (audiCode: ConfiguredCarCarCode) => void) => {
			if (audiCode.id && !reloadAudicode) {
				callback(audiCode);
			} else {
				try {
					dispatchAudiCode({} as ConfiguredCarCarCode, true);

					const result = await fetchAudicode({
						variables: {
							configuredCar: carIdentifier,
							financingInput,
						},
					});

					if (result.data?.configuredCar.code.id) {
						dispatchAudiCode(result.data?.configuredCar.code ?? ({} as ConfiguredCarCarCode), false);
						callback(result.data?.configuredCar.code ?? ({} as ConfiguredCarCarCode));
					}
				} catch (error) {
					console.error('Failed to fetch Audi code:', error);
					dispatchAudiCode({} as ConfiguredCarCarCode, false);
				}
			}
			setReloadAudicode(false);
		},
		[audiCode, dispatchAudiCode, carIdentifier, financingInput, reloadAudicode],
	);

	return { getAudiCode, isLoadingAudiCode, shouldDisableButton };
}
